import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Button, Badge, Modal } from "react-bootstrap";
import api from "../../api";
import { LoadingState } from "../../components/ui/LoadingState";
import { ErrorState } from "../../components/ui/ErrorState";
import {
  formatDate,
  formatDateOrDash,
  statusMeta,
  PHASE_STATUS_META,
  PHASE_STATUS_ORDER,
  MILESTONE_STATUS_META,
} from "./projectWorkspaceUtils";

const phaseStatusOf = (phase) => phase.phase_status || phase.status;

// Compact legend (task 4) -- every possible phase status, in the same
// fixed order everywhere else in this tab uses (PHASE_STATUS_ORDER), each
// with both its icon AND its label so meaning never depends on color alone.
// Also doubles as a summary: each status shows how many phases are in it,
// and statuses with no phases are dimmed.
function TimelineLegend({ phases }) {
  const counts = (phases || []).reduce((acc, p) => {
    const s = phaseStatusOf(p);
    acc[s] = (acc[s] || 0) + 1;
    return acc;
  }, {});
  return (
    <div className="pw-timeline-legend">
      <div className="pw-timeline-legend-list" role="list" aria-label="Phase status legend">
      {PHASE_STATUS_ORDER.map((key) => {
        const meta = PHASE_STATUS_META[key];
        const count = counts[key] || 0;
        return (
          <span
            key={key}
            role="listitem"
            className={`pw-timeline-legend-item pw-timeline-legend-item-${meta.tone}${count === 0 ? " pw-timeline-legend-item-empty" : ""}`}
          >
            <span aria-hidden="true">{meta.icon}</span> {meta.label}
            <span className="pw-timeline-legend-count">{count}</span>
          </span>
        );
      })}
      </div>
      <span className="pw-timeline-bar-key" aria-label="How to read a bar">
        <span><i className="key-done" />Done</span>
        <span><i className="key-progress" />In progress</span>
        <span><i className="key-blocked" />Blocked</span>
        <span><i className="key-planned" />Planned window</span>
        <span><i className="key-milestone" />Milestone</span>
      </span>
    </div>
  );
}

// Month tick marks between the padded range ends. Long projects step by
// quarter so labels never crowd each other.
function buildMonthTicks(range) {
  const months = range.max.diff(range.min, "month");
  const step = months > 18 ? 3 : 1;
  const ticks = [];
  let cursor = range.min.startOf("month").add(1, "month");
  while (!cursor.isAfter(range.max)) {
    ticks.push(cursor);
    cursor = cursor.add(step, "month");
  }
  return ticks;
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

// Plain-language "where is this phase in time" chip, computed only from the
// phase's own planned/actual dates relative to the server's `today`.
function phaseTiming(phase, today) {
  const t = dayjs(today);
  const start = phase.planned_start_date ? dayjs(phase.planned_start_date) : null;
  const end = phase.planned_end_date ? dayjs(phase.planned_end_date) : null;
  if (phaseStatusOf(phase) === "COMPLETED") {
    const doneOn = phase.actual_end_date || phase.planned_end_date;
    return { tone: "success", text: doneOn ? `Done · ${dayjs(doneOn).format("DD MMM")}` : "Done" };
  }
  if (start && start.isAfter(t, "day")) {
    return { tone: "secondary", text: `Starts in ${plural(start.diff(t, "day"), "day")}` };
  }
  if (end && end.isBefore(t, "day")) {
    return { tone: "danger", text: `${plural(t.diff(end, "day"), "day")} overdue` };
  }
  if (end) {
    const left = end.diff(t, "day");
    return { tone: left <= 3 ? "warning" : "info", text: left === 0 ? "Ends today" : `${plural(left, "day")} left` };
  }
  return null;
}

// Top-of-tab summary: the handful of numbers a reader wants before scanning
// the chart. Every figure is a sum/lookup over the fetched payload.
function TimelineSummary({ data }) {
  const phases = data.phases || [];
  const today = dayjs(data.today);

  const totals = phases.reduce(
    (acc, p) => {
      acc.total += p.task_summary?.total || 0;
      acc.completed += p.task_summary?.completed || 0;
      return acc;
    },
    { total: 0, completed: 0 }
  );
  const workPct = totals.total > 0 ? Math.round((totals.completed / totals.total) * 100) : null;
  const phasesDone = phases.filter((p) => phaseStatusOf(p) === "COMPLETED").length;

  const pStart = data.projectStartDate ? dayjs(data.projectStartDate) : null;
  const pEnd = data.projectEndDate ? dayjs(data.projectEndDate) : null;
  let timePct = null;
  let daysLeft = null;
  if (pStart && pEnd && pEnd.isAfter(pStart)) {
    const span = pEnd.diff(pStart, "day");
    timePct = Math.min(100, Math.max(0, Math.round((today.diff(pStart, "day") / span) * 100)));
    daysLeft = pEnd.diff(today, "day");
  }
  const behind = workPct !== null && timePct !== null && timePct - workPct >= 10;

  const current =
    phases.find((p) => {
      if (phaseStatusOf(p) === "COMPLETED" || !p.planned_start_date || !p.planned_end_date) return false;
      return !dayjs(p.planned_start_date).isAfter(today, "day") && !dayjs(p.planned_end_date).isBefore(today, "day");
    }) || phases.find((p) => phaseStatusOf(p) !== "COMPLETED");
  const currentTiming = current ? phaseTiming(current, data.today) : null;

  const nextMilestone = (data.milestones || [])
    .filter((m) => !m.actual_date && m.planned_date && !dayjs(m.planned_date).isBefore(today, "day"))
    .sort((a, b) => dayjs(a.planned_date).diff(dayjs(b.planned_date)))[0];
  const overdueMilestones = (data.milestones || []).filter((m) => m.status === "OVERDUE").length;

  return (
    <div className="pw-tl-summary">
      <div className="pw-tl-card">
        <div className="pw-tl-card-label">Work done</div>
        <div className="pw-tl-card-value">{workPct !== null ? `${workPct}%` : "—"}</div>
        <div className="pw-tl-card-bar"><span style={{ width: `${workPct || 0}%` }} /></div>
        <div className="pw-tl-card-sub">{totals.completed} of {plural(totals.total, "task")} completed</div>
      </div>

      <div className={`pw-tl-card${behind ? " pw-tl-card-warn" : ""}`}>
        <div className="pw-tl-card-label">Time used</div>
        <div className="pw-tl-card-value">{timePct !== null ? `${timePct}%` : "—"}</div>
        <div className="pw-tl-card-bar pw-tl-card-bar-time"><span style={{ width: `${timePct || 0}%` }} /></div>
        <div className="pw-tl-card-sub">
          {daysLeft === null ? "Project dates not set"
            : daysLeft >= 0 ? `${plural(daysLeft, "day")} left · ends ${pEnd.format("DD MMM YYYY")}`
            : `Ended ${plural(-daysLeft, "day")} ago`}
        </div>
        {behind && <div className="pw-tl-card-flag">▲ Work is behind the time used</div>}
      </div>

      <div className="pw-tl-card">
        <div className="pw-tl-card-label">Phases</div>
        <div className="pw-tl-card-value">{phasesDone}<small> / {phases.length}</small></div>
        <div className="pw-tl-card-sub">completed</div>
        {current && (
          <div className="pw-tl-card-sub mt-1">
            <span className="pw-tl-card-now">{`Now: ${current.name}`}</span>
            {currentTiming && <span className={`pw-tl-timing pw-tl-timing-${currentTiming.tone} ms-1`}>{currentTiming.text}</span>}
          </div>
        )}
      </div>

      <div className={`pw-tl-card${overdueMilestones > 0 ? " pw-tl-card-warn" : ""}`}>
        <div className="pw-tl-card-label">Next milestone</div>
        {nextMilestone ? (
          <>
            <div className="pw-tl-card-value pw-tl-card-value-sm">{nextMilestone.name}</div>
            <div className="pw-tl-card-sub">
              {formatDate(nextMilestone.planned_date)} ·{" "}
              {(() => {
                const d = dayjs(nextMilestone.planned_date).diff(today, "day");
                return d === 0 ? "today" : `in ${plural(d, "day")}`;
              })()}
            </div>
          </>
        ) : (
          <div className="pw-tl-card-sub">No upcoming milestone</div>
        )}
        {overdueMilestones > 0 && <div className="pw-tl-card-flag">! {plural(overdueMilestones, "milestone")} overdue</div>}
      </div>
    </div>
  );
}

// Floating detail card that follows the cursor over a phase bar. Rendered
// position: fixed so the timeline's own scroll container never clips it.
function PhaseHoverCard({ hover, milestones, today }) {
  if (!hover) return null;
  const { phase, x, y } = hover;
  const meta = statusMeta(PHASE_STATUS_META, phaseStatusOf(phase));
  const s = phase.task_summary || {};
  const timing = phaseTiming(phase, today);
  const days = phase.planned_start_date && phase.planned_end_date
    ? dayjs(phase.planned_end_date).diff(dayjs(phase.planned_start_date), "day") + 1
    : null;
  const vw = typeof window !== "undefined" ? window.innerWidth : 1200;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const style = {
    left: x > vw - 340 ? x - 324 : x + 16,
    top: y > vh - 300 ? Math.max(8, y - 290) : y + 16,
  };
  return (
    <div className="pw-tl-hover" style={style} role="tooltip">
      <div className="pw-tl-hover-head">
        <strong>{phase.name}</strong>
        <span className={`pw-phase-status-flag pw-phase-status-flag-${meta.tone}`}>
          <span aria-hidden="true">{meta.icon}</span> {meta.label}
        </span>
      </div>
      <div className="pw-tl-hover-row">
        <span>Planned</span>
        <b>
          {formatShortRange(phase.planned_start_date, phase.planned_end_date) || "Not scheduled"}
          {days ? ` · ${plural(days, "day")}` : ""}
        </b>
      </div>
      {timing && (
        <div className="pw-tl-hover-row">
          <span>Timing</span>
          <b className={`pw-tl-hover-timing-${timing.tone}`}>{timing.text}</b>
        </div>
      )}
      {s.total > 0 && (
        <>
          <div className="pw-tl-hover-row">
            <span>Tasks</span>
            <b>{s.completed} of {s.total} done ({Math.round((s.completed / s.total) * 100)}%)</b>
          </div>
          <div className="pw-tl-hover-segbar">
            <i className="seg-done" style={{ width: `${(s.completed / s.total) * 100}%` }} />
            <i className="seg-progress" style={{ width: `${((s.in_progress || 0) / s.total) * 100}%` }} />
            <i className="seg-blocked" style={{ width: `${((s.blocked || 0) / s.total) * 100}%` }} />
          </div>
          <div className="pw-tl-hover-counts">
            <span><i className="dot dot-done" />{s.completed} done</span>
            <span><i className="dot dot-progress" />{s.in_progress || 0} in progress</span>
            <span><i className="dot dot-pending" />{s.pending || 0} pending</span>
            {s.blocked > 0 && <span><i className="dot dot-blocked" />{s.blocked} blocked</span>}
          </div>
        </>
      )}
      <div className="pw-tl-hover-row">
        <span>Owner</span>
        <b>{phase.owner_name || "Unassigned"}</b>
      </div>
      {milestones.length > 0 && (
        <div className="pw-tl-hover-row">
          <span>Milestone{milestones.length === 1 ? "" : "s"}</span>
          <b>
            {milestones.map((m) => (
              <div key={m.id}>
                ◆ {m.name} · {formatDate(m.planned_date) || "not scheduled"}
                {m.status && ` · ${statusMeta(MILESTONE_STATUS_META, m.status).label}`}
              </div>
            ))}
          </b>
        </div>
      )}
      <div className="pw-tl-hover-foot">Click the bar for full details</div>
    </div>
  );
}

function formatShortRange(start, end) {
  if (!start || !dayjs(start).isValid()) return null;
  const s = dayjs(start);
  const e = end && dayjs(end).isValid() ? dayjs(end) : null;
  if (!e) return s.format("DD MMM");
  return `${s.format("DD MMM")} – ${e.format("DD MMM")}`;
}

// The per-phase status flag (task 3) -- a small clickable button (never
// color alone: icon + text label always shown) that opens the phase detail
// popover (task 5). Placed right after the phase name (option A from the
// spec) rather than inside the timeline track, so it can never overlap the
// planned/actual bars or milestone diamonds.
function PhaseStatusFlag({ phase, onClick }) {
  const status = phase.phase_status || phase.status;
  const meta = statusMeta(PHASE_STATUS_META, status);
  const delaySuffix = status === "DELAYED" && phase.delay_days > 0 ? ` · ${phase.delay_days}d` : "";
  return (
    <button
      type="button"
      className={`pw-phase-status-flag pw-phase-status-flag-${meta.tone}`}
      onClick={onClick}
      aria-haspopup="dialog"
    >
      <span aria-hidden="true">{meta.icon}</span> {meta.label}
      {delaySuffix}
    </button>
  );
}

// Phase detail popover (task 5), implemented as a small react-bootstrap
// Modal -- the same "click to see details" pattern already used elsewhere
// in this app (e.g. ProjectDependencyModal.jsx) and, unlike an
// OverlayTrigger/Popover anchored inside the horizontally-scrolling
// timeline track, never fights that container's own scroll/clipping.
// Every field rendered here comes straight from the already-fetched phase
// object (task 2's backend enrichment) -- nothing is invented client-side,
// and no "reason" text is ever synthesized.
function PhaseDetailModal({ phase, onClose }) {
  if (!phase) return null;
  const status = phase.phase_status || phase.status;
  const meta = statusMeta(PHASE_STATUS_META, status);
  const summary = phase.task_summary;
  const hasProgressPercent = phase.progress_percent !== null && phase.progress_percent !== undefined;

  return (
    <Modal show onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>{phase.name}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="pw-phase-modal-status mb-3">
          <span>Status</span>
          <Badge bg={meta.tone}>
            <span aria-hidden="true">{meta.icon}</span> {meta.label}
          </Badge>
        </div>

        <div className="pw-phase-modal-row">
          <span>Planned</span>
          <strong>{formatDateOrDash(phase.planned_start_date)} – {formatDateOrDash(phase.planned_end_date)}</strong>
        </div>

        <div className="pw-phase-modal-row">
          <span>Actual</span>
          <strong>
            Started: {formatDateOrDash(phase.actual_start_date)}
            <br />
            Completed: {formatDateOrDash(phase.actual_end_date)}
          </strong>
        </div>

        {summary && (
          <div className="pw-phase-modal-row">
            <span>Progress</span>
            <strong>
              {summary.completed} / {summary.total} task{summary.total === 1 ? "" : "s"} completed
              {hasProgressPercent && ` (${Math.round(phase.progress_percent)}%)`}
            </strong>
          </div>
        )}

        {summary && summary.pending > 0 && (
          <div className="pw-phase-modal-row">
            <span>Pending</span>
            <strong>{summary.pending} task{summary.pending === 1 ? "" : "s"}</strong>
          </div>
        )}

        {status === "DELAYED" && phase.delay_days > 0 && (
          <div className="pw-phase-modal-row">
            <span>Delay</span>
            <strong>{phase.delay_days} day{phase.delay_days === 1 ? "" : "s"}</strong>
          </div>
        )}

        {status === "COMPLETED" && (
          <div className="pw-phase-modal-row">
            <span>Completion</span>
            <strong>
              {phase.schedule_label
                || (!phase.actual_end_date
                  ? "Completed"
                  : phase.completed_late ? `Completed ${phase.delay_days} day${phase.delay_days === 1 ? "" : "s"} late` : "Completed on time")}
            </strong>
          </div>
        )}

        <div className="pw-phase-modal-row">
          <span>Owner</span>
          <strong>{phase.owner_name || "Unassigned"}</strong>
        </div>

        {Array.isArray(phase.pending_task_titles) && phase.pending_task_titles.length > 0 && (
          <div className="pw-phase-modal-row">
            <span>Pending work</span>
            <ul className="pw-phase-modal-task-list">
              {phase.pending_task_titles.map((title, i) => <li key={i}>{title}</li>)}
            </ul>
          </div>
        )}

        {Array.isArray(phase.blocked_task_titles) && phase.blocked_task_titles.length > 0 && (
          <div className="pw-phase-modal-row">
            <span>Blocked</span>
            <ul className="pw-phase-modal-task-list">
              {phase.blocked_task_titles.map((title, i) => <li key={i}>{title}</li>)}
            </ul>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

// Hand-rolled horizontal timeline (dayjs date math + CSS only -- no
// charting/gantt library was added, per the constraint). One row per
// phase: a lighter "planned" bar plus a thinner darker "actual" overlay
// bar, a red vertical TODAY line, and diamond milestone markers positioned
// by date. Any phase/milestone with a null date renders an explicit
// "Not scheduled" chip instead of fabricating a bar/marker position.
export default function ProjectWorkspaceTimelineTab({ projectId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [hover, setHover] = useState(null);

  const load = () => {
    setLoading(true);
    setError("");
    api
      .get(`/projects/${projectId}/timeline`)
      .then((res) => setData(res.data?.data || null))
      .catch((err) => setError(err.response?.data?.message || "Failed to load timeline"))
      .finally(() => setLoading(false));
  };

  useEffect(load, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  const range = useMemo(() => {
    if (!data) return null;
    const dates = [];
    const pushIfValid = (v) => { if (v && dayjs(v).isValid()) dates.push(dayjs(v)); };
    pushIfValid(data.projectStartDate);
    pushIfValid(data.projectEndDate);
    pushIfValid(data.today);
    (data.phases || []).forEach((p) => {
      pushIfValid(p.planned_start_date);
      pushIfValid(p.planned_end_date);
      pushIfValid(p.actual_start_date);
      pushIfValid(p.actual_end_date);
    });
    (data.milestones || []).forEach((m) => {
      pushIfValid(m.planned_date);
      pushIfValid(m.actual_date);
    });
    if (dates.length === 0) return null;
    let min = dates.reduce((a, b) => (a.isBefore(b) ? a : b));
    let max = dates.reduce((a, b) => (a.isAfter(b) ? a : b));
    // Pad by a few days each side so bars/markers never sit flush at 0%/100%.
    min = min.subtract(2, "day");
    max = max.add(2, "day");
    const totalDays = Math.max(max.diff(min, "day"), 1);
    return { min, max, totalDays };
  }, [data]);

  const pctForDate = (value) => {
    if (!range || !value || !dayjs(value).isValid()) return null;
    const days = dayjs(value).diff(range.min, "day");
    return Math.min(100, Math.max(0, (days / range.totalDays) * 100));
  };

  const barStyle = (startValue, endValue) => {
    const startPct = pctForDate(startValue);
    const endPct = pctForDate(endValue) ?? startPct;
    if (startPct === null) return null;
    const left = startPct;
    const width = Math.max(endPct - startPct, 1.5);
    return { left: `${left}%`, width: `${width}%` };
  };

  if (loading) return <LoadingState message="Loading timeline…" />;
  if (error) return <ErrorState message={error} action={<Button onClick={load}>Retry</Button>} />;
  if (!data?.configured) {
    return (
      <div className="pw-not-configured">
        <i className="fa-solid fa-timeline" aria-hidden="true"></i>
        <h5>Project process not configured</h5>
        <p className="text-muted">Configure the process from the Process tab to see a timeline.</p>
      </div>
    );
  }
  if (!range) {
    return <div className="pw-not-configured"><p className="text-muted">No dated phases or milestones to plot yet.</p></div>;
  }

  const todayPct = pctForDate(data.today);
  const monthTicks = buildMonthTicks(range);

  return (
    <div>
      {(data.projectStartDate || data.projectEndDate) && (
        <div className="pw-timeline-project-range small mb-2">
          <span className="fw-semibold">Project:</span> {formatDateOrDash(data.projectStartDate)} – {formatDateOrDash(data.projectEndDate)}
        </div>
      )}
      <TimelineSummary data={data} />
      <TimelineLegend phases={data.phases} />
      <div className="pw-timeline-scroll">
      <div className="pw-timeline-inner">
        <div className="pw-timeline-header">
          {monthTicks.map((tick, i) => (
            <span key={tick.valueOf()} className="pw-timeline-month-label" style={{ left: `${pctForDate(tick)}%` }}>
              {i === 0 || tick.month() === 0 ? tick.format("MMM YYYY") : tick.format("MMM")}
            </span>
          ))}
          {todayPct !== null && (
            <span className="pw-timeline-today-label" style={{ left: `${todayPct}%` }}>
              TODAY · {dayjs(data.today).format("DD MMM")}
            </span>
          )}
        </div>

        <div className="pw-timeline-body">
        {/* One overlay behind every row, so the past shading, month gridlines
            and TODAY line run unbroken from the header to the last phase. */}
        <div className="pw-timeline-overlay" aria-hidden="true">
          {todayPct !== null && <div className="pw-timeline-past" style={{ width: `${todayPct}%` }} />}
          {monthTicks.map((tick) => (
            <div key={tick.valueOf()} className="pw-timeline-gridline" style={{ left: `${pctForDate(tick)}%` }} />
          ))}
          {todayPct !== null && <div className="pw-timeline-today-line" style={{ left: `${todayPct}%` }} />}
        </div>

        {data.phases.length === 0 ? (
          <p className="text-muted small">No phases defined.</p>
        ) : (
          data.phases.map((phase) => {
            const plannedStyle = barStyle(phase.planned_start_date, phase.planned_end_date);
            const actualStyle = barStyle(phase.actual_start_date, phase.actual_end_date);
            const phaseMilestones = (data.milestones || []).filter((m) => m.project_phase_id === phase.id);
            const tone = statusMeta(PHASE_STATUS_META, phaseStatusOf(phase)).tone;
            const hasProgress = phase.progress_percent !== null && phase.progress_percent !== undefined;
            const progress = hasProgress ? Math.min(100, Math.max(0, Math.round(phase.progress_percent))) : null;
            const dateRange = formatShortRange(phase.planned_start_date, phase.planned_end_date);
            const summary = phase.task_summary;
            // "D01 - Foundation…" -> code chip "D01" + name; names without a
            // short "CODE - " prefix render unchanged.
            const codeMatch = /^([A-Za-z]{0,3}\d{1,3})\s*-\s*(.+)$/.exec(phase.name || "");
            const timing = phaseTiming(phase, data.today);
            const barEndPct = plannedStyle ? parseFloat(plannedStyle.left) + parseFloat(plannedStyle.width) : null;

            return (
              <div key={phase.id}>
                <div className={`pw-timeline-row pw-timeline-row-${tone}`}>
                  <div className="pw-timeline-row-label">
                    <span className="pw-timeline-row-label-name">
                      {codeMatch ? (
                        <>
                          <span className={`pw-timeline-phase-code pw-timeline-phase-code-${tone}`}>{codeMatch[1]}</span>
                          {codeMatch[2]}
                        </>
                      ) : phase.name}
                    </span>
                    {(dateRange || hasProgress) && (
                      <span className="pw-timeline-row-label-meta">
                        {dateRange}
                        {dateRange && hasProgress && " · "}
                        {hasProgress && `${progress}%`}
                        {summary && summary.total > 0 && ` (${summary.completed}/${summary.total})`}
                      </span>
                    )}
                    <span className="pw-timeline-row-flags">
                      <PhaseStatusFlag phase={phase} onClick={() => setSelectedPhase(phase)} />
                      {timing && <span className={`pw-tl-timing pw-tl-timing-${timing.tone}`}>{timing.text}</span>}
                    </span>
                  </div>
                  <div className="pw-timeline-track">
                    {plannedStyle ? (
                      <div
                        className={`pw-timeline-bar pw-timeline-bar-${tone}`}
                        style={plannedStyle}
                        onClick={() => setSelectedPhase(phase)}
                        onMouseMove={(e) => setHover({ phase, x: e.clientX, y: e.clientY })}
                        onMouseLeave={() => setHover(null)}
                      >
                        {/* Done segment, then in-progress (striped) and blocked
                            segments, so the bar shows where the work actually is. */}
                        {summary && summary.total > 0 ? (
                          <>
                            <div className="pw-timeline-bar-fill" style={{ width: `${(summary.completed / summary.total) * 100}%` }} />
                            <div className="pw-timeline-bar-seg-progress" style={{ width: `${((summary.in_progress || 0) / summary.total) * 100}%` }} />
                            <div className="pw-timeline-bar-seg-blocked" style={{ width: `${((summary.blocked || 0) / summary.total) * 100}%` }} />
                          </>
                        ) : (
                          hasProgress && <div className="pw-timeline-bar-fill" style={{ width: `${progress}%` }} />
                        )}
                      </div>
                    ) : (
                      <span className="pw-timeline-not-scheduled">Not scheduled</span>
                    )}
                    {plannedStyle && hasProgress && (
                      <span
                        className={`pw-timeline-bar-pct pw-timeline-bar-pct-${tone}`}
                        style={barEndPct > 80 ? { right: `calc(${100 - parseFloat(plannedStyle.left)}% + 10px)` } : { left: `calc(${barEndPct}% + 14px)` }}
                      >
                        {progress}%
                        {summary && summary.total > 0 && (
                          <span className="pw-timeline-bar-pct-sub">
                            {summary.completed}/{summary.total} done
                            {summary.in_progress > 0 && ` · ${summary.in_progress} active`}
                            {summary.blocked > 0 && ` · ${summary.blocked} blocked`}
                          </span>
                        )}
                      </span>
                    )}
                    {actualStyle && (
                      <div className="pw-timeline-bar pw-timeline-bar-actual" style={actualStyle} title={`Actual: ${formatDate(phase.actual_start_date)} – ${formatDate(phase.actual_end_date)}`} />
                    )}
                    {phaseMilestones.map((m) => {
                      const mPct = pctForDate(m.planned_date || m.actual_date);
                      if (mPct === null) return null;
                      const mMeta = statusMeta(MILESTONE_STATUS_META, m.status);
                      return (
                        <div
                          key={m.id}
                          className={`pw-timeline-milestone pw-timeline-milestone-${mMeta.tone}`}
                          style={{ left: `${mPct}%` }}
                          title={`${m.name} — ${formatDate(m.planned_date) || "not scheduled"}${m.status ? ` · ${mMeta.label}` : ""}`}
                        />
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
        </div>

        {(data.milestones || []).some((m) => !m.project_phase_id) && (
          <div className="mt-3">
            <div className="fw-semibold small mb-2">Unscoped Milestones</div>
            {data.milestones.filter((m) => !m.project_phase_id).map((m) => (
              <div key={m.id} className="d-flex align-items-center gap-2 small mb-1">
                <i className="fa-solid fa-flag-checkered text-muted" aria-hidden="true"></i>
                <span>{m.name}</span>
                <span className="text-muted">{formatDate(m.planned_date) || "Not scheduled"}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
      <PhaseHoverCard
        hover={hover}
        milestones={hover ? (data.milestones || []).filter((m) => m.project_phase_id === hover.phase.id) : []}
        today={data.today}
      />
      <PhaseDetailModal phase={selectedPhase} onClose={() => setSelectedPhase(null)} />
    </div>
  );
}
